import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';

import { ExpandableResource } from '../expandable-resource';
import { Command, CommandSet } from './commands.model';

const isContainerPresent = ({ isContainer }: ExpandableResource<any>) => isContainer;

const filteredAllowedActions = (commands, { singleObject = false, runningOnRoot = false }) => {
  let allowedCommands = singleObject ? commands : commands.filter(({ isSingleObjectAction }) => !isSingleObjectAction);

  allowedCommands = runningOnRoot
    ? allowedCommands.filter(({ isNestedActionOnly }) => !isNestedActionOnly)
    : allowedCommands;
  return allowedCommands;
};

@Component({
  selector: 'miq-commands',
  templateUrl: './commands.component.html',
  styleUrls: ['./commands.component.scss'],
  standalone: false,
})
export class CommandsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() resources: Array<ExpandableResource<any>>;
  @Input() commands: CommandSet = { defaults: [] };
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  @Input() errorCallback: (error: unknown) => void = () => {};
  @Output() namedEvent = new EventEmitter();
  @Output() rowChange = new EventEmitter();

  allowedCommands: Command[];
  currentActiveCommand: Command;

  ngOnInit() {
    /* required by label translation feature */
  }

  ngOnChanges({ resources, commands }: SimpleChanges) {
    if ((resources && resources.currentValue) || commands) {
      if (this.resources.length > 1) {
        this.rowChange.emit();
      }
      this.computeCommands();
    }
  }

  private computeCommands() {
    const {
      defaults = [],
      mixed: mixedCommands = [],
      leafs: leafsCommands = [],
      containers: containerCommands = [],
    } = this.commands;

    const atLeastOneContainer = this.resources.some(isContainerPresent);
    const atLeastOneLeaf = this.resources.some(resource => !isContainerPresent(resource));
    const isMixedContent = atLeastOneLeaf && atLeastOneContainer;

    const allowedCommands: Command[] = isMixedContent
      ? mixedCommands
      : atLeastOneLeaf && !atLeastOneContainer
        ? [...leafsCommands, ...mixedCommands]
        : !atLeastOneLeaf && atLeastOneContainer
          ? [...containerCommands, ...mixedCommands]
          : defaults;

    const runningOnRoot = this.resources.every(({ level }) => level === 0);
    const allowancesOptions = { singleObject: this.resources.length === 1, runningOnRoot };
    this.allowedCommands = filteredAllowedActions(allowedCommands, allowancesOptions).map(command => ({
      ...command,
      active: false,
      visible: command.isVisible && allowancesOptions.singleObject ? command.isVisible(this.resources[0]) : true,
      className:
        command.className && typeof command.className === 'function'
          ? command.className(this.resources)
          : command.className,
    }));
  }

  applyCommand(commandItem: Command) {
    if (!commandItem.isDisabled) {
      commandItem.active = true;
      this.applyCommandFlow(commandItem);
      this.currentActiveCommand = commandItem;
    }
  }

  applyActiveCommand(commandItem: Command) {
    commandItem.active = false;
    this.applyCommandFlow({ command: commandItem.onActiveCommand });
    this.currentActiveCommand = null;
  }

  private applyCommandFlow({ command }: { command: string | Function }) {
    if (typeof command === 'string') {
      return this.namedEvent.emit(command);
    }
    try {
      return command(this.resources);
    } catch (e) {
      this.errorCallback(e != null && e.message ? e.message : e);
    }
  }

  ngOnDestroy() {
    /* label translation purpose added */
  }
}
